import useEvaluationConfiguration from 'Hooks/useEvaluationConfiguration';
import useTranslation from 'Hooks/useTranslation';
import useSwitchTabDetector from './useSwitchTabDetector';
import { defaultValues } from '../schemas/evaluation-schema';
import { CalificationI, EvaluationAnswerFormT } from 'types';
import lessonApi from 'Api/axios/lesson-api';
import useLessonQuestions from 'Hooks/useLessonQuestions';
import { useEffect, useState } from 'react';
import useLessonApi from 'Hooks/useLessonApi';

/**
 * Maneja toda la lógica del inicio del examen.
 *
 * @see {@link test-start.tsx} Página de inicio del examen.
 * @param lessonId - Id de la lección
 */
const useTestStartPage = (lessonId: string) => {
  const { t2 } = useTranslation('course_test');

  const [calification, setCalification] = useState<CalificationI | null>(null);

  /**
   * Custom hook para obtener la configuración del examen
   */
  const { configuration } = useEvaluationConfiguration({
    lessonId,
    init: true,
  });

  const { data: lesson } = useLessonApi({
    lessonId,
    init: true,
  });

  useEffect(() => {
    lessonApi.checkIfUserHasAlreadyTakenTheTest(lessonId).then(response => {
      if (response.data) {
        setCalification(response.data);
      }
    });
  }, [lessonId]);

  /**
   * Custom hook para obtener las preguntas de una lección de tipo `evaluación automática`.
   */
  const { data: questions } = useLessonQuestions({ lessonId, init: true });

  /**
   * Custom hook para prevenir que el usuario cambie de pestaña si
   * la configuración del examen lo indica.
   */
  useSwitchTabDetector({
    onTabSwitch: () => {
      if (configuration?.preventTabSwitching && !calification) {
        alert(t2('you_changed_tabs'));

        // Mandamos las respuestas vacias al servidor para indicar que el usuario intentó cambiar de pestaña
        // reprobando con esta acción.
        onSubmit(defaultValues);
      }
    },
    isActive: true,
  });

  /**
   * Envia las respuestas al servidor para finalizar el exámen.
   * @param data - Respuestas del empleado.
   */
  const onSubmit = async (answers: Array<EvaluationAnswerFormT>) => {
    const body = {
      lessonId,
      answers,
    };

    const response = await lessonApi.sendAnswers(body);

    if (response.data) {
      setCalification(response.data);
    }
  };

  return {
    t2,
    lesson,
    onSubmit,
    questions,
    calification,
    configuration,
  };
};

export default useTestStartPage;
